import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import imageStore from '../../../assets/avatar/store.png';

import LoadingSkeletonModalTable from '@/components/modal/prompt/LoadingSkeletonModalTable';
import DetailStoreCard from '@/components/panel/store/DetailStoreCard';
import HagglesTable from '@/components/table/haggle-event/HagglesTable';
import ProductsTable from '@/components/table/store/ProductsTable';
import BackButton from '@/components/ui/button/BackButton';
import { DEFAULT_PAGE_SIZE } from '@/components/utils/constants';
import { formatDate } from '@/components/utils/utils';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { IStore } from '@/interfaces/api/IStore';
import { IProduct } from '@/interfaces/api/product/IProduct';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const StoreDetails = () => {
	const { storeTradeName } = useParams<{ storeTradeName: string }>();

	const [selectedTab, setSelectedTab] = useState('haggles');

	const {
		data: store,
		isLoading: storeIsLoading,
		isError: storeIsError,
		error: storeError,
	} = useQuery<IStore>({
		queryKey: [ApiQueryKeys.getStoreByTradeName, storeTradeName],
		queryFn: () => haggleApiService.getStoreByTradeName(storeTradeName),
		enabled: storeTradeName !== undefined && typeof storeTradeName === 'string',
	});

	const { data: products, refetch: refetchProducts } = useQuery({
		queryKey: [ApiQueryKeys.getStoreProductsWithHaggleByUserId, storeTradeName],
		queryFn: () =>
			haggleApiService.getStoreProductsWithHaggleByUserId(store!.users[0].id, {
				params: {
					'page[size]': DEFAULT_PAGE_SIZE,
					'page[number]': 1,
				},
			}),
		enabled: store !== undefined,
		refetchOnWindowFocus: false,
	});

	if (storeIsError) {
		if (storeError instanceof Error) {
			notificationService.error(storeError.message);
		} else {
			notificationService.error('Unknown error fetching store');
		}
	}

	return (
		<>
			<div className="m-[0.5rem]">
				<BackButton />
			</div>
			<div className="flex flex-col gap-4">
				{storeIsLoading && <LoadingSkeletonModalTable />}
				{store && !storeIsLoading && (
					<>
						<DetailStoreCard
							title={store.tradeName}
							subTitle={`# ID ${store.id}`}
							avatar={imageStore}
							data={[
								{
									name: 'Date added',
									value: formatDate(store.createdAt),
								},
							]}
						/>

						<div className="flex justify-around flex-wrap">
							{/* <div className="w-[200px]">
								<button
									className={`leading-5 font-medium w-full py-[10px] text-[1.3rem] ${
										selectedTab === 'users' ? 'bg-stone-300' : ''
									}`}
									disabled={selectedTab === 'users'}
									onClick={() => setSelectedTab('users')}
									data-test="users-store-tab-button"
								>
									Users
								</button>
							</div> */}

							<div className="w-[200px]">
								<button
									className={`leading-5 font-medium w-full py-[10px] text-[1.3rem] ${
										selectedTab === 'haggles' ? 'bg-stone-300' : ''
									}`}
									disabled={selectedTab === 'haggles'}
									onClick={() => setSelectedTab('haggles')}
									data-test="haggles-store-tab-button"
								>
									Haggles
								</button>
							</div>

							<div className="w-[200px]">
								<button
									className={`leading-5 font-medium w-full py-[10px] text-[1.3rem] ${
										selectedTab === 'products' ? 'bg-stone-300' : ''
									}`}
									disabled={selectedTab === 'products'}
									onClick={() => setSelectedTab('products')}
									data-test="products-store-tab-button"
								>
									Products
								</button>
							</div>
						</div>

						{selectedTab === 'haggles' && (
							<HagglesTable haggles={store.haggleEvents} />
						)}

						{selectedTab === 'products' && (
							<ProductsTable
								products={products?.data as IProduct[]}
								refetchProducts={refetchProducts}
							/>
						)}
					</>
				)}
			</div>
		</>
	);
};

export default StoreDetails;
